import React, { useState, useEffect } from 'react';
import { Table, Modal, message, Input } from 'antd';
import { api, getAdminErrorMessage } from '../lib/api';
import { showBulkActionFeedback } from '../lib/bulkActionFeedback';
import BulkActionModal from '../components/BulkActionModal';

const { TextArea } = Input;
const TASK_REVIEW_VIEW_KEY = 'admin_task_review_view';

const pillStyle = (backgroundColor, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  backgroundColor,
  color,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px',
  letterSpacing: '0.01em'
});

const buttonStyle = (variant = 'neutral', disabled = false) => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    },
    success: {
      background: 'linear-gradient(135deg, #34c759 0%, #159947 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(21, 153, 71, 0.2)'
    },
    danger: {
      background: 'linear-gradient(135deg, #ff5f6d 0%, #d9363e 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(217, 54, 62, 0.2)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: disabled ? 'none' : style.boxShadow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    opacity: disabled ? 0.7 : 1
  };
};

const TaskReview = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [keyword, setKeyword] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState({ open: false, action: null });
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TASK_REVIEW_VIEW_KEY) || 'null');
      if (!saved) return;
      setKeyword(saved.keyword || '');
      setReviewFilter(saved.reviewFilter || 'all');
      setTypeFilter(saved.typeFilter || 'all');
      setPageSize(saved.pageSize || 10);
      setSortBy(saved.sortBy || 'createdAt');
      setSortDirection(saved.sortDirection || 'desc');
    } catch (_) {
      localStorage.removeItem(TASK_REVIEW_VIEW_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTasks();
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, reviewFilter, typeFilter, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem(TASK_REVIEW_VIEW_KEY, JSON.stringify({
      keyword,
      reviewFilter,
      typeFilter,
      pageSize,
      sortBy,
      sortDirection
    }));
  }, [keyword, reviewFilter, typeFilter, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [keyword, reviewFilter, typeFilter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/tasks', {
        params: {
          keyword: keyword.trim() || undefined,
          reviewStatus: reviewFilter === 'all' ? undefined : reviewFilter,
          type: typeFilter === 'all' ? undefined : typeFilter,
          page,
          pageSize,
          sortBy,
          sortDirection
        }
      });
      setTasks(response.data.tasks || []);
      setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0 });
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载失败'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      await api.post(`/api/admin/tasks/${taskId}/approve`, {});
      message.success('审核通过');
      fetchTasks();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleApprovePayout = async (taskId) => {
    Modal.confirm({
      title: '确认审核放款',
      content: '请确认接单凭证、发布者确认结单记录无异常。通过后赏金会从发布者冻结余额转入接单者钱包。',
      okText: '确认放款',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post(`/api/admin/tasks/${taskId}/payout/approve`, {});
          message.success('已放款到接单者钱包');
          fetchTasks();
        } catch (error) {
          message.error(getAdminErrorMessage(error, '放款失败'));
        }
      }
    });
  };

  const handleDeleteTask = (record) => {
    Modal.confirm({
      title: '确认删除发布信息',
      content: record.type === 'bounty'
        ? '删除后该发布信息会从列表移除，未进入处理流程的赏金冻结金额会退回发布者钱包。'
        : '删除后该发布信息会从列表移除，用户端也不再展示。',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingTaskId(record.id);
        try {
          await api.delete(`/api/admin/tasks/${record.id}`);
          message.success('已删除发布信息');
          setSelectedRowKeys((keys) => keys.filter((id) => id !== record.id));
          if (selectedTask?.id === record.id) {
            setDetailVisible(false);
            setSelectedTask(null);
          }
          fetchTasks();
        } catch (error) {
          message.error(getAdminErrorMessage(error, '删除失败'));
        } finally {
          setDeletingTaskId(null);
        }
      }
    });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning('请输入拒绝原因');
      return;
    }
    try {
      await api.post(`/api/admin/tasks/${selectedTask.id}/reject`, {
        reason: rejectReason
      });
      message.success('已拒绝');
      setRejectVisible(false);
      setRejectReason('');
      fetchTasks();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const openBulkReviewModal = (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择任务');
      return;
    }
    setBulkAction({ open: true, action });
    setBulkRejectReason('');
  };

  const submitBulkReview = async (ids) => {
    const normalizedIds = ids || selectedRowKeys;
    if (!bulkAction.action || normalizedIds.length === 0) return;
    if (bulkAction.action === 'reject' && !bulkRejectReason.trim()) {
      message.warning('请输入批量拒绝原因');
      return;
    }
    setBulkSubmitting(true);
    try {
      const response = await api.post('/api/admin/tasks/bulk-review', {
        ids: normalizedIds,
        action: bulkAction.action,
        reason: bulkAction.action === 'reject' ? bulkRejectReason.trim() : undefined
      });
      showBulkActionFeedback(response.data, {
        entityLabel: '任务',
        successVerb: '审核',
        onSelectFailed: response.data.failedIds?.length ? (failedIds) => setSelectedRowKeys(failedIds.map((id) => Number(id))) : undefined,
        onRetry: response.data.failedIds?.length ? submitBulkReview : undefined
      });
      if (!response.data.failedIds?.length) {
        setSelectedRowKeys([]);
      }
      setBulkAction({ open: false, action: null });
      setBulkRejectReason('');
      fetchTasks();
    } catch (error) {
      message.error(error.response?.data?.message || '批量处理失败');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkReview = async () => {
    await submitBulkReview();
  };

  const resetFilters = () => {
    setKeyword('');
    setReviewFilter('all');
    setTypeFilter('all');
    setSortBy('createdAt');
    setSortDirection('desc');
    setPage(1);
    setPageSize(10);
    localStorage.removeItem(TASK_REVIEW_VIEW_KEY);
  };

  const sortLabel = getTaskSortLabel(sortBy, sortDirection);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: true,
      sortOrder: sortBy === 'id' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      ellipsis: true,
      sorter: true,
      sortOrder: sortBy === 'title' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      sorter: true,
      sortOrder: sortBy === 'type' ? toAntdSortOrder(sortDirection) : null,
      render: (type) => (
        <span style={pillStyle(type === 'bounty' ? '#FFF3E0' : '#EAFBF1', type === 'bounty' ? '#C77700' : '#157347')}>
          {type === 'bounty' ? '赏金' : '免费'}
        </span>
      )
    },
    {
      title: '金额',
      dataIndex: 'reward',
      key: 'reward',
      width: 80,
      sorter: true,
      sortOrder: sortBy === 'reward' ? toAntdSortOrder(sortDirection) : null,
      render: (reward) => `¥${reward}`
    },
    {
      title: '状态',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      width: 90,
      sorter: true,
      sortOrder: sortBy === 'reviewStatus' ? toAntdSortOrder(sortDirection) : null,
      render: (reviewStatus) => {
        const config = {
          pending: { bg: '#FFF7E6', color: '#FA8C16', text: '待审核' },
          approved: { bg: '#F6FFED', color: '#52C41A', text: '已通过' },
          rejected: { bg: '#FFF1F0', color: '#FF4D4F', text: '已拒绝' }
        };
        const style = config[reviewStatus];
        return (
          <span style={pillStyle(style?.bg, style?.color)}>
            {style?.text}
          </span>
        );
      }
    },
    {
      title: '流程',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const meta = getTaskFlowMeta(status);
        return <span style={pillStyle(meta.bg, meta.color)}>{meta.text}</span>;
      }
    },
    {
      title: '发布者',
      dataIndex: ['author', 'username'],
      key: 'author',
      width: 100
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setSelectedTask(record);
              setDetailVisible(true);
            }}
            style={buttonStyle('neutral')}
          >
            查看
          </button>
          {record.reviewStatus === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(record.id)}
                style={buttonStyle('success')}
              >
                通过
              </button>
              <button
                onClick={() => {
                  setSelectedTask(record);
                  setRejectVisible(true);
                }}
                style={buttonStyle('danger')}
              >
                拒绝
              </button>
            </>
          )}
          {record.status === 'PENDING_PAYOUT' && (
            <button
              onClick={() => handleApprovePayout(record.id)}
              style={buttonStyle('primary')}
            >
              审核放款
            </button>
          )}
          {record.status === 'OPEN' && (
            <button
              disabled={deletingTaskId === record.id}
              onClick={() => handleDeleteTask(record)}
              style={buttonStyle('danger', deletingTaskId === record.id)}
            >
              {deletingTaskId === record.id ? '删除中' : '删除'}
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '8px', background: 'transparent', minHeight: '100%' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2a44', marginBottom: '6px' }}>
          任务审核
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          集中处理待审核任务，快速查看发布信息与审核状态。
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{
          flex: '1 1 220px',
          padding: '24px',
          background: 'linear-gradient(135deg, #FFF7E6 0%, #FFE7BA 100%)',
          textAlign: 'center',
          borderRadius: '16px',
          boxShadow: '0 14px 30px rgba(250, 140, 22, 0.12)'
        }}>
          <div style={{ fontSize: '14px', color: '#8C8C8C', marginBottom: '8px' }}>待审核</div>
          <div style={{ fontSize: '32px', color: '#FA8C16', fontWeight: '500' }}>{stats.pending}</div>
        </div>
        <div style={{
          flex: '1 1 220px',
          padding: '24px',
          background: 'linear-gradient(135deg, #F6FFED 0%, #D9F7BE 100%)',
          textAlign: 'center',
          borderRadius: '16px',
          boxShadow: '0 14px 30px rgba(82, 196, 26, 0.12)'
        }}>
          <div style={{ fontSize: '14px', color: '#8C8C8C', marginBottom: '8px' }}>已通过</div>
          <div style={{ fontSize: '32px', color: '#52C41A', fontWeight: '500' }}>{stats.approved}</div>
        </div>
        <div style={{
          flex: '1 1 220px',
          padding: '24px',
          background: 'linear-gradient(135deg, #FFF1F0 0%, #FFCCC7 100%)',
          textAlign: 'center',
          borderRadius: '16px',
          boxShadow: '0 14px 30px rgba(255, 77, 79, 0.12)'
        }}>
          <div style={{ fontSize: '14px', color: '#8C8C8C', marginBottom: '8px' }}>已拒绝</div>
          <div style={{ fontSize: '32px', color: '#FF4D4F', fontWeight: '500' }}>{stats.rejected}</div>
        </div>
      </div>

      <div style={toolbarStyle}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索标题、描述、分类、发布者"
          allowClear
          style={filterInputStyle}
        />
        <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部审核状态</option>
          <option value="pending">待审核</option>
          <option value="pending_payout">待平台结算审核</option>
          <option value="approved">已通过</option>
          <option value="rejected">已拒绝</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部任务类型</option>
          <option value="bounty">赏金</option>
          <option value="free">免费</option>
        </select>
        <button onClick={() => openBulkReviewModal('approve')} style={buttonStyle('success')}>批量通过</button>
        <button onClick={() => openBulkReviewModal('reject')} style={buttonStyle('danger')}>批量拒绝</button>
        <button onClick={resetFilters} style={buttonStyle('neutral')}>恢复默认</button>
        <div style={toolbarHintStyle}>
          {selectedRowKeys.length > 0 ? `已选 ${selectedRowKeys.length} 条 · ` : ''}
          当前排序：{sortLabel} · 共 {total} 条
        </div>
      </div>

      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderRadius: '18px',
        boxShadow: '0 20px 45px rgba(31, 42, 68, 0.08)',
        overflow: 'hidden'
      }}>
        <Table
          columns={columns}
          dataSource={tasks}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.reviewStatus !== 'pending'
            })
          }}
          onChange={(pagination, _, sorter) => {
            const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setPage(pagination.current || 1);
            setPageSize(pagination.pageSize || 10);
            if (nextSorter?.field) {
              setSortBy(String(nextSorter.field));
              setSortDirection(nextSorter.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSortBy('createdAt');
              setSortDirection('desc');
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }
          }}
          scroll={{ x: 1000 }}
          size="middle"
          bordered={false}
        />
      </div>

      <Modal
        title="任务详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          <button
            onClick={() => setDetailVisible(false)}
            style={buttonStyle('primary')}
          >
            关闭
          </button>
        }
        width={600}
      >
        {selectedTask && (
          <div style={modalBodyStyle}>
            <DetailItem label="标题" value={selectedTask.title} />
            <DetailItem
              label="类型"
              value={
                <span style={pillStyle(selectedTask.type === 'bounty' ? '#FFF3E0' : '#EAFBF1', selectedTask.type === 'bounty' ? '#C77700' : '#157347')}>
                  {selectedTask.type === 'bounty' ? '赏金' : '免费'}
                </span>
              }
            />
            <DetailItem label="金额" value={`¥${selectedTask.reward}`} />
            <DetailItem label="分类" value={selectedTask.category} />
            <DetailItem label="地点" value={selectedTask.location || '无'} />
            <DetailItem label="截止时间" value={selectedTask.deadline || '无'} />
            <DetailItem label="联系方式" value={selectedTask.contactInfo || '无'} />
            <DetailItem label="流程状态" value={getTaskFlowMeta(selectedTask.status).text} />
            <DetailItem label="接单者" value={selectedTask.assignee?.nickname || selectedTask.assignee?.username || '未接单'} />
            <DetailItem label="完成者" value={selectedTask.completedBy?.nickname || selectedTask.completedBy?.username || '未结单'} />
            <DetailItem
              label="接单凭证"
              multiline
              value={(selectedTask.proofs || []).length > 0
                ? (selectedTask.proofs || []).map((proof) => `${proof.fileName}${proof.textContent ? `：${proof.textContent}` : ''}`).join('\n')
                : '暂无凭证'}
            />
            <DetailItem label="详情" multiline value={selectedTask.description} />
          </div>
        )}
      </Modal>

      <Modal
        title="拒绝任务"
        open={rejectVisible}
        onCancel={() => {
          setRejectVisible(false);
          setRejectReason('');
        }}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setRejectVisible(false);
                setRejectReason('');
              }}
              style={buttonStyle('neutral')}
            >
              取消
            </button>
            <button
              onClick={handleReject}
              style={buttonStyle('danger')}
            >
              确认拒绝
            </button>
          </div>
        }
      >
        <TextArea
          rows={4}
          placeholder="请输入拒绝原因"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          style={textAreaStyle}
        />
      </Modal>

      <BulkActionModal
        open={bulkAction.open}
        title={bulkAction.action === 'approve' ? '批量通过任务' : '批量拒绝任务'}
        confirmText={bulkAction.action === 'approve' ? '确认批量通过' : '确认批量拒绝'}
        confirmVariant={bulkAction.action === 'approve' ? 'success' : 'danger'}
        confirmLoading={bulkSubmitting}
        onCancel={() => {
          setBulkAction({ open: false, action: null });
          setBulkRejectReason('');
        }}
        onConfirm={handleBulkReview}
        summaryLines={[
          `选中任务：${selectedRowKeys.length} 条`,
          `处理动作：${bulkAction.action === 'approve' ? '批量通过' : '批量拒绝'}`
        ]}
        note={bulkAction.action === 'approve' ? '仅待审核任务可被批量通过。' : '批量拒绝会把同一原因写入所有选中任务，请确保原因表述适用于这一批任务。'}
      >
        {bulkAction.action === 'reject' && (
          <div>
            <div style={fieldLabelStyle}>拒绝原因</div>
            <TextArea
              rows={4}
              placeholder="请输入批量拒绝原因"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              style={textAreaStyle}
            />
          </div>
        )}
      </BulkActionModal>
    </div>
  );
};

const modalBodyStyle = {
  display: 'grid',
  gap: '12px'
};

const toolbarStyle = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 18px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  borderRadius: '16px',
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const filterInputStyle = {
  flex: '1 1 280px',
  minWidth: '220px'
};

const filterSelectStyle = {
  minWidth: '170px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid #d6e4ff',
  backgroundColor: '#f8fbff',
  color: '#1f2a44'
};

const toolbarHintStyle = {
  marginLeft: 'auto',
  color: '#6b7280',
  fontSize: '13px',
  fontWeight: 600
};

const toAntdSortOrder = (direction) => direction === 'asc' ? 'ascend' : 'descend';

const getTaskSortLabel = (sortBy, sortDirection) => {
  const labels = {
    createdAt: '创建时间',
    id: 'ID',
    title: '标题',
    type: '类型',
    reward: '金额',
    reviewStatus: '审核状态'
  };
  return `${labels[sortBy] || '创建时间'}${sortDirection === 'asc' ? '升序' : '降序'}`;
};

const getTaskFlowMeta = (status) => ({
  OPEN: { bg: '#EAF4FF', color: '#1677ff', text: '待接单' },
  IN_PROGRESS: { bg: '#FFF7E6', color: '#FA8C16', text: '进行中' },
  SUBMITTED: { bg: '#FFF7E6', color: '#D46B08', text: '待结单审核' },
  PENDING_PAYOUT: { bg: '#F0F5FF', color: '#2F54EB', text: '待平台结算审核' },
  COMPLETED: { bg: '#F6FFED', color: '#52C41A', text: '已完成' },
  CANCELLED: { bg: '#F5F5F5', color: '#595959', text: '已取消' }
}[status] || { bg: '#F5F5F5', color: '#595959', text: status || '-' });

const detailCardStyle = (multiline = false) => ({
  padding: multiline ? '14px 16px' : '12px 16px',
  backgroundColor: '#f8fbff',
  borderRadius: '14px',
  border: '1px solid #e6eefc',
  whiteSpace: multiline ? 'pre-wrap' : 'normal',
  wordBreak: 'break-word',
  color: '#1f2a44',
  lineHeight: 1.7
});

const DetailItem = ({ label, value, multiline = false }) => (
  <div>
    <div style={{ fontSize: '12px', color: '#7b8794', marginBottom: '6px', fontWeight: 600 }}>{label}</div>
    <div style={detailCardStyle(multiline)}>{value}</div>
  </div>
);

const textAreaStyle = {
  border: '1px solid #d6e4ff',
  borderRadius: '14px',
  padding: '10px 12px',
  backgroundColor: '#f8fbff'
};

export default TaskReview;
